import { of, merge } from "rxjs";
import { filter, delay, takeUntil } from "rxjs/operators";
import { isActionOf } from "typesafe-actions";
import "@opentelemetry/api";
import "../../otel/TraceProvider.js";
import "react";
import "react-error-boundary";
import { switchMap, mergeMap } from "../../otel/rxjs.js";
import "@opentelemetry/context-zone";
import "@opentelemetry/exporter-trace-otlp-http";
import "@opentelemetry/instrumentation";
import "@opentelemetry/instrumentation-document-load";
import "@opentelemetry/instrumentation-xml-http-request";
import "@opentelemetry/resources";
import "@opentelemetry/sdk-trace-web";
import "@opentelemetry/semantic-conventions";
import { BaseEpicsBuilder } from "../epicsBuilder.js";
import "../actions.js";
import "../../api/types.js";
import "immer";
import "moment-timezone";
import "react-intl";
import "@apollo/client/errors";
import "@apollo/client";
import "graphql";
import "@apollo/client/link/subscriptions";
import "@apollo/client/utilities";
import "cross-fetch";
import "graphql-ws";
import "../../node_modules/moment/dist/moment.js";
import "rxjs/ajax";
import "fast-deep-equal/es6";
import "redux";
import { getEntityActions } from "../entity/actions.js";
import "../entity/epics.js";
import { EntityKeysMapper } from "../entity/schemas.js";
import { entitiesSelector } from "../entity/selectors.js";
import "../entity/EntityProvider.js";
import { getRealtimeActions } from "../realtime/actions.js";
import "normalizr";
import { getViewActions } from "./actions.js";
import { viewsSelector, createViewSelector } from "./selectors.js";
import "react-redux";
import "react-router";
import "../realtime/RealtimeNotification.js";
class ViewEpicsBuilder extends BaseEpicsBuilder {
  _viewActions;
  _entityActions;
  _realtimeActions;
  _schemaMapper;
  _entityTypes;
  constructor(schemaMapper, entityTypes) {
    super();
    this._viewActions = getViewActions();
    this._entityActions = getEntityActions();
    this._realtimeActions = getRealtimeActions();
    this._schemaMapper = schemaMapper;
    this._entityTypes = entityTypes;
  }
  buildEpicList() {
    return [
      this.buildInitEpic(),
      this.buildUpdateFilterEpic(),
      this.buildUpdateKeysEpic(),
      this.buildUpdateEpic(),
      this.buildMarkUnusedEpic(),
      this.buildCloseEpic(),
      this.buildRefreshSubscriptionEpic(),
      this.buildPauseEpic(),
      this.buildResumeEpic()
    ];
  }
  buildInitEpic() {
    const initEpic = (action$, state$) => action$.pipe(
      filter(isActionOf(this._viewActions.init)),
      switchMap((action) => {
        const resultActions = [];
        const {
          view: viewType,
          displayName,
          entity,
          realtime,
          filter: filter2,
          blockFetchWithInvalidFilter,
          cleanDelay,
          isCancelable
        } = action.payload;
        const views = viewsSelector(state$.value);
        const view = views?.[viewType];
        if (!view || !view?.entities?.[entity.entity]) {
          resultActions.push(
            this._viewActions.save({
              view: viewType,
              displayName,
              entity,
              realtime
            }),
            this._viewActions.updateFilter({
              view: viewType,
              entity: entity.entity,
              filter: filter2,
              blockFetchWithInvalidFilter,
              isCancelable
            })
          );
        } else {
          resultActions.push(
            ...view?.entities ? Object.entries(view.entities).flatMap(([_, value]) => {
              const entityType = value.entity;
              return this._viewActions.update({
                entity: entityType,
                views: [viewType],
                blockFetchWithInvalidFilter
              });
            }) : []
          );
        }
        if (realtime) {
          resultActions.push(
            this._realtimeActions.subscribe({
              entity: realtime.entity,
              view: viewType,
              triggers: realtime.triggers
            })
          );
        }
        resultActions.push(
          this._viewActions.markUsed({
            view: viewType
          }),
          this._viewActions.updateCleanDelay({
            view: viewType,
            cleanDelay
          })
        );
        resultActions.push(
          this._viewActions.changeIsInitialized({
            view: viewType,
            entity: entity.entity,
            isInitialized: true
          })
        );
        return of(...resultActions);
      })
    );
    return initEpic;
  }
  buildUpdateFilterEpic() {
    const updateFilterEpic = (action$, state$) => action$.pipe(
      filter(isActionOf(this._viewActions.updateFilter)),
      switchMap((action) => {
        const viewType = action.payload.view;
        const view = createViewSelector(viewType)(state$.value);
        const entities = view?.entities;
        if (entities) {
          const updatedEntity = Object.values(entities).find(
            (i) => i.entity === action.payload.entity
          );
          const entityFetchIsBlocked = this.getFetchIsBlocked(
            action.payload.filter,
            action.payload.blockFetchWithInvalidFilter
          );
          const resultActions = [
            this._viewActions.saveFilter(action.payload),
            !entityFetchIsBlocked ? this._entityActions.fetch({
              ...this.mapEntityToFetchActionPayload(updatedEntity),
              filter: action.payload.filter,
              isCancelable: action.payload.isCancelable
            }) : null
          ].filter((i) => i);
          return of(...resultActions);
        }
        return of();
      })
    );
    return updateFilterEpic;
  }
  buildUpdateEpic() {
    const updateEpic = (action$, state$) => action$.pipe(
      filter(isActionOf(this._viewActions.update)),
      switchMap((action) => {
        const { views, entity, blockFetchWithInvalidFilter } = action.payload;
        const updateViewTypes = views;
        const allViews = viewsSelector(state$.value);
        const updatedEntities = updateViewTypes.map((viewType) => allViews[viewType]?.entities?.[entity]).filter((updatedEntity) => updatedEntity);
        const resultActions = updatedEntities ? [
          ...updatedEntities.map((updatedEntity) => {
            const fetchIsBlocked = this.getFetchIsBlocked(updatedEntity.filter, blockFetchWithInvalidFilter);
            return !fetchIsBlocked ? this._entityActions.fetch(this.mapEntityToFetchActionPayload(updatedEntity)) : null;
          }).filter((i) => i)
        ] : [];
        return updatedEntities ? of(...resultActions) : of();
      })
    );
    return updateEpic;
  }
  buildUpdateKeysEpic() {
    return (action$, state$) => action$.pipe(
      filter(isActionOf(this._viewActions.updateKeys)),
      switchMap((a) => {
        const resultActions = [];
        const { entity, keys, filter: filter2 } = a.payload;
        const views = viewsSelector(state$.value);
        const entities = entitiesSelector(state$.value);
        const affectedViews = this.getAffectedViews(views, entity, filter2);
        affectedViews.forEach((view) => {
          const oldKeys = this.getViewKeys(view, entities);
          const updatedView = { ...view, entities: { ...view.entities, [entity]: { ...view.entities[entity], keys } } };
          const newKeys = this.getViewKeys(updatedView, entities);
          const newReferences = [...new Set(newKeys.filter((k) => !oldKeys.includes(k)))];
          if (newReferences?.length) {
            resultActions.push(this._entityActions.increaseReferenceCounter({ references: newReferences }));
          }
          const deletedReferences = [...new Set(oldKeys.filter((k) => !newKeys.includes(k)))];
          if (deletedReferences?.length) {
            resultActions.push(this._entityActions.decreaseReferenceCounter({ references: deletedReferences }));
          }
        });
        return of(...resultActions, this._viewActions.saveKeys(a.payload));
      })
    );
  }
  buildCloseEpic() {
    const closeEpic = (action$, state$) => action$.pipe(
      filter(isActionOf(this._viewActions.close)),
      mergeMap((action) => {
        const resultActions = [];
        const { view: viewType } = action.payload;
        const views = viewsSelector(state$.value);
        const view = views[viewType];
        const updatedEntities = Object.values(view.entities).map(
          (entity) => this._viewActions.changeIsInitialized({
            view: viewType,
            entity: entity.entity,
            isInitialized: false
          })
        );
        resultActions.push(...updatedEntities);
        const realtimes = view?.realtime ? Object.values(view.realtime) : [];
        resultActions.push(
          ...realtimes.map(
            (realtime) => this._realtimeActions.unsubscribe({
              entity: realtime.entity,
              view: viewType,
              triggers: realtime.triggers
            })
          )
        );
        return merge(
          of(...resultActions),
          of(
            this._viewActions.markUnused({
              view: viewType
            })
          ).pipe(
            delay(view?.cleanDelay ?? 0),
            takeUntil(
              action$.pipe(
                filter((a) => {
                  return isActionOf(this._viewActions.markUsed)(a) && a.payload.view === viewType;
                })
              )
            )
          )
        );
      })
    );
    return closeEpic;
  }
  buildMarkUnusedEpic() {
    const markUnusedEpic = (action$, state$) => action$.pipe(
      filter(isActionOf(this._viewActions.markUnused)),
      mergeMap((action) => {
        const resultActions = [];
        const { view: viewType } = action.payload;
        const views = viewsSelector(state$.value);
        const view = views[viewType];
        const entities = entitiesSelector(state$.value);
        if (view) {
          const oldKeys = this.getViewKeys(view, entities);
          const deletedReferences = [...new Set(oldKeys)];
          if (deletedReferences?.length) {
            resultActions.push(this._entityActions.decreaseReferenceCounter({ references: deletedReferences }));
          }
        }
        resultActions.push(
          this._viewActions.clean({
            view: viewType
          })
        );
        return of(...resultActions);
      })
    );
    return markUnusedEpic;
  }
  buildPauseEpic() {
    const epic = (action$, state$) => action$.pipe(
      filter(isActionOf(this._viewActions.pause)),
      switchMap(() => {
        const views = viewsSelector(state$.value);
        const viewsWithRealtime = Object.values(views).filter((v) => v.realtime);
        const resultActions = viewsWithRealtime.flatMap((v) => {
          return Object.values(v.realtime).map(
            (realtime) => this._realtimeActions.unsubscribe({
              view: v.viewType,
              entity: realtime.entity,
              triggers: realtime.triggers,
              pauseResume: true
            })
          );
        });
        return of(...resultActions);
      })
    );
    return epic;
  }
  buildResumeEpic() {
    const epic = (action$, state$) => action$.pipe(
      filter(isActionOf(this._viewActions.resume)),
      switchMap(() => {
        const views = viewsSelector(state$.value);
        const viewsWithRealtime = Object.values(views).filter((v) => v.realtime);
        const resultActions = viewsWithRealtime.flatMap((v) => {
          return Object.values(v.realtime).map(
            (realtime) => this._realtimeActions.subscribe({
              view: v.viewType,
              entity: realtime.entity,
              triggers: realtime.triggers,
              pauseResume: true
            })
          );
        });
        return of(...resultActions);
      })
    );
    return epic;
  }
  buildRefreshSubscriptionEpic() {
    const epic = (action$, state$) => action$.pipe(
      filter(isActionOf(this._viewActions.refreshSubscription)),
      switchMap((action) => {
        const resultActions = [];
        const payload = action.payload;
        const viewType = payload?.view;
        const realtime = payload?.realtime;
        const entityType = payload?.realtime?.entity;
        const views = viewsSelector(state$.value);
        const view = views?.[viewType];
        const prevRealtime = view?.realtime?.[entityType];
        if (JSON.stringify(prevRealtime) !== JSON.stringify(realtime)) {
          resultActions.push(
            this._viewActions.save({
              entity: view.entities[entityType],
              view: viewType,
              displayName: view.displayName,
              realtime
            })
          );
          if (prevRealtime) {
            resultActions.push(
              this._realtimeActions.unsubscribe({
                entity: prevRealtime.entity,
                view: viewType,
                triggers: prevRealtime.triggers
              })
            );
          }
          if (realtime) {
            resultActions.push(
              this._realtimeActions.subscribe({
                ...realtime,
                view: viewType
              })
            );
          }
        }
        return of(...resultActions);
      })
    );
    return epic;
  }
  getAffectedViews(views, type, filter2) {
    return views && type ? Object.values(views).filter((v) => v?.entities[type]?.filter === filter2) : [];
  }
  getViewKeys(view, entities) {
    const keysAdapter = new EntityKeysMapper(this._schemaMapper, this._entityTypes);
    return view?.entities ? Object.entries(view.entities).flatMap(([_, value]) => {
      const entityType = value.entity;
      const viewKeys = value.keys ?? [];
      return viewKeys.flatMap((key) => {
        return this.getUniqueKeys(entities, entityType, key).map((k) => keysAdapter.mapToStringKey(k));
      });
    }) : [];
  }
  getUniqueKeys(entities, parentType, parentId) {
    const keysAdapter = new EntityKeysMapper(this._schemaMapper, this._entityTypes);
    const result = [];
    const entityState = entities?.entities[parentType];
    if (entityState && entityState[parentId]) {
      const keys = keysAdapter.getUniqueKeyObjects(entityState[parentId], parentType);
      result.push(...keys);
      keys.forEach(({ type, id }) => {
        if (type !== parentType || id !== parentId) {
          result.push(...this.getUniqueKeys(entities, type, id));
        }
      });
    }
    return result;
  }
  mapEntityToFetchActionPayload(entity) {
    return {
      type: entity.entity,
      filter: entity.filter,
      fields: entity.fields
    };
  }
  getFetchIsBlocked(filter2, blockWithInvalidFilter) {
    const filterObject = new URLSearchParams(filter2);
    const invalidFilterKey = "invalid";
    const invalidFilterValue = filterObject.get(invalidFilterKey);
    return blockWithInvalidFilter && invalidFilterValue === "true";
  }
}
export {
  ViewEpicsBuilder
};
//# sourceMappingURL=epics.js.map
